/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnInit, AfterViewInit } from '@angular/core';
import {
	Matter,
	MatterAdjustmentDeposit,
	MatterClientType,
	MatterFileType,
	MatterPropertyType,
	PropertyStatus,
	PropertyType,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { Observable, Subscription } from 'rxjs';
import { Select } from '@ngxs/store';
import { ApsKeysService } from '@repos/aps-keys/aps-keys.service';
import { MatterAiJobServiceRepo } from '@repos/matter-ai-job/matter-ai-job.service';
import { APSKey } from '@models/aps-key';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterDocumentType } from '@models/matter-file';
import { DepositSideFormMode } from '../../fields/deposit/side-form/sale-deposit-side-form.component';
import { DepositHolderDropdownMode } from '../../fields/deposit-holder-dropdown/deposit-holder-dropdown.component';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { MatterMortgage } from '@models/mortgage';
import { MatDialog } from '@angular/material/dialog';
import { VtbMortgageDeleteModalComponent } from './vtb-mortgage-delete-modal/vtb-mortgage-delete-modal.component';
import { take } from 'rxjs/operators';
import { TestHooks } from './aps-side-form.hooks';
import { DocumentAiDataService } from '@services/document-ai/document-ai-data.service';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import {
	LawyerSearchModels,
	LawyerSearchState,
} from '@state/algolia-search/lawyer-search';
import { PersonClient } from '@models/clients';
import { ClientsState } from '@state/clients';
import { DocumentProcessorService } from '@services/document-ai/document-processor.service';
import { NavigationService } from '@services/nav/nav.service';

export const APS_SIDE_FORM_FRAGMENTS = {
	AGREEMENT_DATE: 'agreement-date',
	CLOSING_DATE: 'closing-date',
	TITLE_SEARCH_COMPLETION_DATE: 'title-search-completion-date',
	KEY_DATES: 'key-dates',
	FINANCIALS: 'financials',
	CLIENTS: 'clients',
	BUYER: 'buyer',
	SELLER: 'seller',
	ADDRESS: 'address',
	OTHER_LAWYER: 'other-lawyer',
	PROPERTY_TYPE: 'property-type',
	SELLER_BROKERAGE: 'seller-brokerage',
	SELLER_AGENT_BROKERAGE: 'seller-agent-brokerage',
	BUYER_BROKERAGE: 'buyer-brokerage',
	DEPOSIT: 'deposit',
	PURCHASE_PRICE: 'purchase-price',
	CONDO: 'condo',
};

export interface Position {
	position: number;
	page: number;
}

@UntilDestroy()
@Component({
	selector: 'app-aps-side-form',
	templateUrl: './aps-side-form.component.html',
	styleUrls: ['./aps-side-form.component.scss'],
	standalone: false,
})
export class ApsSideFormComponent implements OnInit, AfterViewInit {
	testHooks = TestHooks;
	public APS_SIDE_FORM_FRAGMENTS = APS_SIDE_FORM_FRAGMENTS;
	public buyerClientType = MatterClientType.BUYER;
	public sellerClientType = MatterClientType.SELLER;
	public selectedInputField = '';
	public url;
	public scrollTo: Position;
	public saleClientTypes = MatterClientType;
	public clientType: MatterClientType;

	public sale: Matter | null;
	private apsKeysSubscription: Subscription;
	public fileTypes = MatterFileType;
	public APSFileType = MatterDocumentType.APS;
	public showClerk = true;

	public propertyStatus = PropertyStatus;
	public PropertyType = PropertyType;

	public keys: APSKey[];

	public depositSideFormMode = DepositSideFormMode;
	public depositHolderDropdownMode = DepositHolderDropdownMode;

	public loading$ = this.documentProcessorService.isLoading$(
		MatterDocumentType.APS,
	);
	public documentAiData: any;

	@Input() myInput: string;

	@Select(SelectedMatter.getSelected) sale$: Observable<Matter>;
	@SelectSnapshot(SelectedMatter.id) matterId;
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;
	@Select(SelectedMatter.matterFileType)
	matterFileType$: Observable<MatterFileType>;
	@ViewSelectSnapshot(SelectedMatter.otherPartyLawFirm) lawFirm: LawFirm;
	@Select(SelectedMatter.propertyType)
	propertyType$: Observable<MatterPropertyType>;
	@Select(MatterMortgagesState.getVTBMortgage)
	vtbMortgage$: Observable<MatterMortgage>;
	@SelectSnapshot(MatterMortgagesState.getVTBMortgage)
	vtbMortgage: MatterMortgage;
	@Select(AdjustmentsSelector.depositAdjustment)
	depositAdjustment$: Observable<MatterAdjustmentDeposit>;

	@Select(LawyerSearchState.getList) lawyers$: Observable<
		LawyerSearchModels.LayerSearchResult[]
	>;
	@Select(LawyerSearchState.getSearching)
	searchingLawyers$: Observable<boolean>;

	@Select(ClientsState.getAllClients) allClients$: Observable<PersonClient[]>;
	private aiJobSubscription: Subscription;
	private activeAiJobId: string;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	public vtbMortgageExists: boolean = false;
	constructor(
		private apsKeysService: ApsKeysService,
		private mortgageDispatch: MatterMortgageDispatch,
		public documentAiDataService: DocumentAiDataService<Record<string, any>>,
		private matDialog: MatDialog,
		private documentProcessorService: DocumentProcessorService,
		private navService: NavigationService,
		private matterAiJobService: MatterAiJobServiceRepo,
	) {}

	jumpToSection = (ev) => {
		this.documentAiDataService.jumpToSection(ev);

		this.selectedInputField = ev;

		this.scrollTo = { page: 2, position: 300 };

		if (!this.keys) {
			return;
		}

		const pageDetails = this.keys.find((x) => x.key === ev);

		if (!pageDetails) {
			return;
		}

		this.scrollTo = { page: pageDetails.Page - 1, position: pageDetails.Y };
	};

	ngAfterViewInit(): void {
		this.navService.scrollToElement([
			'shadow-xl',
			'shadow-grey-600',
			'rounded-xl',
			'animation',
			'ease-in-out',
		]);
	}

	async ngOnInit(): Promise<void> {
		this.sale$.pipe(untilDestroyed(this)).subscribe((value) => {
			this.url = value?.latestAPS?.url;
			this.sale = value;

			if (
				value?.id &&
				value?.latestAPS?.ranKeys &&
				value?.latestAPS?.id &&
				!this.apsKeysSubscription
			) {
				this.apsKeysSubscription = this.apsKeysService
					.All({ saleId: value.id, apsId: value.latestAPS.id })
					.pipe(untilDestroyed(this))
					.subscribe((value) => {
						this.keys = value as APSKey[];
					});
			}

			if (
				value?.id &&
				value?.latestAPS?.aiJobId &&
				value?.latestAPS?.aiJobId !== this.activeAiJobId
			) {
				this.activeAiJobId = value.latestAPS.aiJobId;

				this.aiJobSubscription?.unsubscribe();

				this.aiJobSubscription = this.matterAiJobService
					.Get$(value.latestAPS.aiJobId, { matterId: value.id })
					.pipe(untilDestroyed(this))
					.subscribe((job) => {
						console.log('JOB DATA', job);

						if (job?.status === 'failed') return;

						if (job?.data && job.status !== 'mapped') {
							console.log('Setting data', job.data);
							this.documentAiDataService.setData({
								...job.data.documentProcessingDto.apsData,
								formDetails: job.data.formDetails,
								aiJobId: job.id,
							});
						}
					});
			}
		});

		this.vtbMortgage$.pipe(untilDestroyed(this)).subscribe((value) => {
			this.vtbMortgageExists = !!value;
		});

		this.documentAiDataService
			.getData$()
			.pipe(untilDestroyed(this))
			.subscribe((data) => {
				if (data === null) return;

				this.documentAiData = data;

				// Update the job
				this.matterAiJobService.Update(
					data.aiJobId,
					{
						isActive: false,
						status: 'mapped',
					},
					{ matterId: this.matterId },
				);
			});
	}

	toggleVTBMortgage(value: boolean) {
		if (value) {
			this.mortgageDispatch.CreateVTBMortgage();
		} else if (!value) {
			// Show confirmation modal before emoving

			this.matDialog
				.open(VtbMortgageDeleteModalComponent)
				.afterClosed()
				.pipe(take(1))
				.subscribe((result) => {
					if (result) {
						this.mortgageDispatch.remove(this.vtbMortgage.id);
					} else {
						// Reset swith back to true since mortgage wasn't removed
						this.vtbMortgageExists = true;
					}
				});
		}
	}
}
